import React, { useState, useRef, useEffect, useCallback } from "react";
import { Mic } from "lucide-react";

const SR = typeof window !== "undefined" ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;
export const speechSupported = !!SR;

// Recording state machine for Web Speech dictation, kept out of the component.
function useDictation({ getValue, onChange }) {
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);
  const cbRef = useRef({ getValue, onChange });
  cbRef.current = { getValue, onChange };

  const stop = useCallback(() => {
    try { recRef.current?.stop(); }
    catch (e) { console.error("Dictation stop failed:", e); }
    setListening(false);
  }, []);

  // stop any in-flight recognition on unmount
  useEffect(() => stop, [stop]);

  const appendTranscript = useCallback((event) => {
    let finalText = "";
    for (let i = event.resultIndex; i < event.results.length; i++) {
      if (event.results[i].isFinal) finalText += event.results[i][0].transcript;
    }
    if (!finalText.trim()) return;
    const { getValue: gv, onChange: oc } = cbRef.current;
    const base = gv() || "";
    const sep = base && !/\s$/.test(base) ? " " : "";
    oc((base + sep + finalText.trim()).replace(/\s+/g, " ").trimStart());
  }, []);

  const start = useCallback(() => {
    if (!SR) return;
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = "en-US";
    rec.onresult = appendTranscript;
    rec.onend = () => setListening(false);
    rec.onerror = (e) => { console.error("Dictation error:", e?.error || e); setListening(false); };
    recRef.current = rec;
    try { rec.start(); setListening(true); }
    catch (e) { console.error("Dictation start failed:", e); setListening(false); }
  }, [appendTranscript]);

  return { listening, start, stop };
}

// Dictation button. Appends final transcript chunks to a bound value.
export default function MicButton({ getValue, onChange, testid, className = "" }) {
  const { listening, start, stop } = useDictation({ getValue, onChange });

  if (!SR) return null;

  const stateClass = listening
    ? "border-[#9E4A41] bg-[#9E4A41]/10 text-[#9E4A41] ai-thinking"
    : "border-stone-300 text-stone-500 hover:border-brand-primary hover:text-brand-primary";

  return (
    <button
      type="button"
      data-testid={testid}
      onClick={listening ? stop : start}
      title={listening ? "Stop dictation" : "Dictate"}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-all duration-300 ${stateClass} ${className}`}
    >
      <Mic size={13} strokeWidth={1.8} />
      {listening ? "Listening\u2026" : "Speak"}
    </button>
  );
}
